import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { AdminGuard } from "../components/admin/AdminGuard";
import { AdminNav } from "../components/admin/AdminNav";
import { Reveal } from "../components/Reveal";
import { ICON_MAP } from "../data/categories";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";

const inputClass =
  "h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";
const textareaClass =
  "w-full rounded-xl border border-input bg-white p-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";

const COLLECTIONS = {
  services: {
    label: "Services", singular: "Service",
    description: "The cards in the Services section, with their outcome tags.",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", required: true, hint: "lowercase-with-dashes, unique" },
      { key: "outcome", label: "Outcome tag", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea", required: true },
      { key: "icon", label: "Icon", type: "icon" },
      { key: "path", label: "Links to", type: "text", placeholder: "/services/…" },
      { key: "order", label: "Display order", type: "number" },
    ],
    title: (d) => d.title,
    sub: (d) => d.outcome,
  },
  testimonials: {
    label: "Testimonials", singular: "Testimonial",
    description: "Client quotes in the social proof section.",
    fields: [
      { key: "name", label: "Client name", type: "text", required: true },
      { key: "role", label: "Role / context", type: "text", placeholder: "e.g. Master's Student, Toronto" },
      { key: "quote", label: "Quote", type: "textarea", required: true },
      { key: "order", label: "Display order", type: "number" },
    ],
    title: (d) => d.name,
    sub: (d) => d.role,
  },
  faqs: {
    label: "FAQs", singular: "FAQ",
    description: "Questions and answers in the accordion section.",
    fields: [
      { key: "question", label: "Question", type: "text", required: true },
      { key: "answer", label: "Answer", type: "textarea", required: true },
      { key: "order", label: "Display order", type: "number" },
    ],
    title: (d) => d.question,
    sub: (d) => (d.answer || "").slice(0, 70),
  },
  "case-studies": {
    label: "Case Studies", singular: "Case study",
    description: "Before/after client transformation stories.",
    fields: [
      { key: "title", label: "Headline", type: "text", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "before", label: "Before", type: "textarea", required: true },
      { key: "after", label: "After", type: "textarea", required: true },
      { key: "outcome", label: "Outcome badge", type: "text" },
      { key: "quote", label: "Client quote", type: "textarea" },
      { key: "order", label: "Display order", type: "number" },
    ],
    title: (d) => d.title,
    sub: (d) => d.category,
  },
  tutors: {
    label: "Tutors", singular: "Tutor",
    description: "Tutor profiles shown on the Online Tutoring page.",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "subject", label: "Subject", type: "text", required: true },
      { key: "bio", label: "Bio", type: "textarea" },
      { key: "tags", label: "Tags", type: "list", hint: "One per line" },
      { key: "rate", label: "Rate ($ / session)", type: "number" },
      { key: "rating", label: "Rating (0–5)", type: "number" },
      { key: "sessions_held", label: "Sessions held", type: "number" },
      { key: "order", label: "Display order", type: "number" },
    ],
    title: (d) => d.name,
    sub: (d) => `${d.subject} · $${d.rate}/session`,
  },
  faculty: {
    label: "Faculty (Tutors page)", singular: "Faculty member",
    description: "Faculty profiles shown on the Tutors page.",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "qualification", label: "Qualification", type: "text", placeholder: "e.g. PhD in English Literature" },
      { key: "institution", label: "Institution", type: "text" },
      { key: "courses", label: "Assigned courses", type: "list", hint: "One per line, e.g. IELTS" },
      { key: "bio", label: "Short bio (optional)", type: "textarea" },
      { key: "order", label: "Display order", type: "number" },
    ],
    title: (d) => d.name,
    sub: (d) => [d.qualification, d.institution].filter(Boolean).join(" · "),
  },
  courses: {
    label: "Courses", singular: "Course",
    description: "The Quranic Studies course catalog.",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "category", label: "Category", type: "select", options: ["Tajweed", "Hifz", "Tafsir", "Arabic"] },
      { key: "level", label: "Level", type: "select", options: ["Beginner", "Intermediate", "Advanced"] },
      { key: "instructor", label: "Instructor", type: "text" },
      { key: "duration", label: "Duration", type: "text", placeholder: "e.g. 8 weeks" },
      { key: "lessons", label: "Lessons", type: "number" },
      { key: "price", label: "Price ($ · 0 = Free)", type: "number" },
      { key: "description", label: "Description", type: "textarea", required: true },
      { key: "outcomes", label: "Outcomes", type: "list", hint: "One per line" },
      { key: "curriculum", label: "Curriculum modules", type: "list", hint: "One per line" },
      { key: "order", label: "Display order", type: "number" },
    ],
    title: (d) => d.title,
    sub: (d) => `${d.category} · ${d.level}`,
  },
};

function FieldInput({ field, value, onChange }) {
  const testId = `content-field-${field.key}`;
  if (field.type === "textarea") {
    return <textarea data-testid={testId} rows={4} className={textareaClass} value={value ?? ""}
      placeholder={field.placeholder || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === "list") {
    return (
      <textarea data-testid={testId} rows={5} className={textareaClass}
        value={Array.isArray(value) ? value.join("\n") : value ?? ""}
        placeholder={field.placeholder || "One item per line"}
        onChange={(e) => onChange(e.target.value)} />
    );
  }
  if (field.type === "select") {
    return (
      <select data-testid={testId} value={value ?? field.options[0]} onChange={(e) => onChange(e.target.value)}
        className={inputClass}>
        {field.options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  if (field.type === "icon") {
    const Icon = ICON_MAP[value] || ICON_MAP.Compass;
    return (
      <div className="flex items-center gap-3">
        <select data-testid={testId} value={value || "Compass"} onChange={(e) => onChange(e.target.value)}
          className={`${inputClass} flex-1`}>
          {Object.keys(ICON_MAP).map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold/10 text-gold-dark">
          <Icon className="h-5 w-5" />
        </span>
      </div>
    );
  }
  return <input data-testid={testId} type={field.type === "number" ? "number" : "text"}
    step={field.type === "number" ? "any" : undefined} className={inputClass}
    value={value ?? ""} placeholder={field.placeholder || ""} onChange={(e) => onChange(e.target.value)} />;
}

function CollectionEditor({ name }) {
  const config = COLLECTIONS[name];
  const [docs, setDocs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/content/${name}`);
      setDocs(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setDocs([]);
    }
  }, [name]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    const nextOrder = docs?.length ? Math.max(...docs.map((d) => d.order || 0)) + 1 : 1;
    const draft = {};
    config.fields.forEach((f) => {
      if (f.key === "order") draft[f.key] = nextOrder;
      else if (f.type === "number") draft[f.key] = 0;
      else if (f.type === "icon") draft[f.key] = "Compass";
      else if (f.type === "select") draft[f.key] = f.options[0];
      else draft[f.key] = "";
    });
    setEditing({ isNew: true, draft });
  };

  const openEdit = (doc) => setEditing({ isNew: false, id: doc.id, draft: { ...doc } });

  const save = async () => {
    for (const f of config.fields) {
      if (f.required && !String(editing.draft[f.key] ?? "").trim()) {
        return toast.error(`${f.label} is required`);
      }
    }
    setSaving(true);
    try {
      const payload = {};
      config.fields.forEach((f) => {
        const v = editing.draft[f.key];
        if (f.type === "number") payload[f.key] = Number(v) || 0;
        else if (f.type === "list") {
          const arr = Array.isArray(v) ? v : String(v ?? "").split("\n");
          payload[f.key] = arr.map((s) => String(s).trim()).filter(Boolean);
        } else payload[f.key] = String(v ?? "");
      });
      if (editing.isNew) await api.post(`/content/${name}`, payload);
      else await api.put(`/content/${name}/${editing.id}`, payload);
      toast.success(editing.isNew ? `${config.singular} added — it's live on the site` : `${config.singular} updated`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (doc) => {
    if (confirmDelete !== doc.id) {
      setConfirmDelete(doc.id);
      setTimeout(() => setConfirmDelete((c) => (c === doc.id ? null : c)), 3000);
      return;
    }
    try {
      await api.delete(`/content/${name}/${doc.id}`);
      toast.success(`${config.singular} deleted`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid={`collection-editor-${name}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-bold text-forest-deep">{config.label}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{config.description}</p>
        </div>
        <button onClick={openNew} data-testid={`content-add-${name}`}
          className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-display text-sm font-bold text-forest-deep transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(212,175,55,0.4)]">
          <Plus className="h-4 w-4" /> Add {config.singular.toLowerCase()}
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {docs === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
        ) : docs.length === 0 ? (
          <p className="rounded-3xl border border-dashed border-border bg-white py-14 text-center text-sm text-muted-foreground" data-testid={`content-empty-${name}`}>
            Nothing here yet — add your first {config.singular.toLowerCase()}.
          </p>
        ) : (
          docs.map((doc) => {
            const Icon = name === "services" ? ICON_MAP[doc.icon] || ICON_MAP.Compass : null;
            return (
              <div key={doc.id} data-testid={`content-row-${doc.id}`}
                className="flex items-center gap-4 rounded-2xl border border-border bg-white px-5 py-4 transition-shadow duration-200 hover:shadow-[0_10px_30px_rgba(10,41,30,0.07)]">
                <span className="hidden w-8 shrink-0 text-center font-mono text-xs font-semibold text-gold-dark sm:block">
                  {String(doc.order ?? 0).padStart(2, "0")}
                </span>
                {Icon && (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-forest/5 text-forest">
                    <Icon className="h-4.5 w-4.5" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-forest-deep">{config.title(doc)}</p>
                  <p className="truncate text-xs text-muted-foreground">{config.sub(doc)}</p>
                </div>
                <button onClick={() => openEdit(doc)} data-testid={`content-edit-${doc.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold text-forest transition-colors hover:border-forest/40">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button onClick={() => remove(doc)} data-testid={`content-delete-${doc.id}`}
                  className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                    confirmDelete === doc.id
                      ? "border-destructive bg-destructive text-white"
                      : "border-border text-destructive/70 hover:border-destructive/50"
                  }`}>
                  <Trash2 className="h-3.5 w-3.5" />
                  {confirmDelete === doc.id ? "Sure?" : "Delete"}
                </button>
              </div>
            );
          })
        )}
      </div>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto bg-bone p-0 sm:max-w-lg" data-testid="content-editor-sheet">
          {editing && (
            <div>
              <div className="bg-forest-deep px-7 py-6">
                <SheetTitle className="font-display text-lg font-bold text-bone">
                  {editing.isNew ? `New ${config.singular.toLowerCase()}` : `Edit ${config.singular.toLowerCase()}`}
                </SheetTitle>
                <p className="mt-1 text-xs text-emerald-100/60">Changes go live on the site the moment you save.</p>
              </div>
              <div className="space-y-5 px-7 py-7">
                {config.fields.map((f) => (
                  <div key={f.key}>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                      {f.label} {f.required && <span className="text-gold-dark">*</span>}
                    </label>
                    <FieldInput field={f} value={editing.draft[f.key]}
                      onChange={(v) => setEditing((e) => ({ ...e, draft: { ...e.draft, [f.key]: v } }))} />
                    {f.hint && <p className="mt-1.5 text-xs text-muted-foreground/70">{f.hint}</p>}
                  </div>
                ))}
                <button onClick={save} data-testid="content-save-btn" disabled={saving}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-forest py-3.5 font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {editing.isNew ? "Publish" : "Save changes"}
                </button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const SOCIAL_KEYS = ["linkedin", "instagram", "facebook", "youtube"];
const CONTACT_KEYS = [
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "address", label: "Office address" },
];

function SiteEditor() {
  const [site, setSite] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/site").then((r) => setSite(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!site) {
    return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>;
  }

  const setContact = (k, v) => setSite((s) => ({ ...s, contact: { ...s.contact, [k]: v } }));
  const setSocial = (k, v) => setSite((s) => ({ ...s, socials: { ...s.socials, [k]: v } }));
  const setMetric = (i, k, v) =>
    setSite((s) => {
      const metrics = [...(s.metrics || [])];
      metrics[i] = { ...metrics[i], [k]: k === "value" || k === "decimals" ? Number(v) || 0 : v };
      return { ...s, metrics };
    });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { mission: site.mission, contact: site.contact, socials: site.socials, metrics: site.metrics };
      await api.put("/content/site", payload);
      toast.success("Site & footer content updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="site-editor">
      <div>
        <h2 className="font-display text-xl font-bold text-forest-deep">Site &amp; Footer</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Brand mission, contact details, social links, and the proof metrics on the landing page.
        </p>
      </div>

      <div className="rounded-3xl border border-border bg-white p-7">
        <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Brand mission (footer)
        </label>
        <textarea data-testid="site-field-mission" rows={3} className={textareaClass}
          value={site.mission || ""} onChange={(e) => setSite((s) => ({ ...s, mission: e.target.value }))} />
      </div>

      <div className="rounded-3xl border border-border bg-white p-7">
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Contact details</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {CONTACT_KEYS.map((c) => (
            <div key={c.key} className={c.key === "address" ? "sm:col-span-2" : ""}>
              <label className="mb-1.5 block text-xs font-semibold text-foreground/60">{c.label}</label>
              <input data-testid={`site-field-${c.key}`} className={inputClass}
                value={site.contact?.[c.key] || ""} onChange={(e) => setContact(c.key, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-3xl border border-border bg-white p-7">
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Social links</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {SOCIAL_KEYS.map((k) => (
            <div key={k}>
              <label className="mb-1.5 block text-xs font-semibold capitalize text-foreground/60">{k}</label>
              <input data-testid={`site-field-social-${k}`} className={inputClass} placeholder="https://…"
                value={site.socials?.[k] || ""} onChange={(e) => setSocial(k, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-3xl border border-border bg-white p-7">
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Proof metrics (landing page counters)
        </p>
        <div className="space-y-3">
          {(site.metrics || []).map((m, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_70px_70px] items-center gap-2.5" data-testid={`site-metric-row-${i}`}>
              <input className={inputClass} value={m.label || ""} placeholder="Label"
                onChange={(e) => setMetric(i, "label", e.target.value)} data-testid={`site-metric-${i}-label`} />
              <input className={inputClass} type="number" step="any" value={m.value ?? 0} placeholder="Value"
                onChange={(e) => setMetric(i, "value", e.target.value)} data-testid={`site-metric-${i}-value`} />
              <input className={inputClass} value={m.prefix || ""} placeholder="$"
                onChange={(e) => setMetric(i, "prefix", e.target.value)} data-testid={`site-metric-${i}-prefix`} />
              <input className={inputClass} value={m.suffix || ""} placeholder="+"
                onChange={(e) => setMetric(i, "suffix", e.target.value)} data-testid={`site-metric-${i}-suffix`} />
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} data-testid="site-save-btn" disabled={saving}
        className="flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save site content
      </button>
    </div>
  );
}

const TABS = [
  ...Object.entries(COLLECTIONS).map(([key, c]) => ({ key, label: c.label })),
  { key: "site", label: "Site & Footer" },
];

export default function AdminContentPage() {
  const [tab, setTab] = useState("services");

  return (
    <AdminGuard>
      <div className="min-h-screen bg-bone pt-[72px]" data-testid="admin-content-page">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">Admin — CMS</p>
            <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-forest-deep sm:text-4xl">
              Content Editor
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Edit what visitors see on the landing page — no code, no redeploy. Changes go live instantly.
            </p>
            <AdminNav />
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-9 flex flex-wrap gap-2" data-testid="content-tabs">
              {TABS.map((t) => (
                <button key={t.key} data-testid={`content-tab-${t.key}`} onClick={() => setTab(t.key)}
                  className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                    tab === t.key
                      ? "border-forest bg-forest text-bone"
                      : "border-border bg-white text-foreground/60 hover:border-forest/40 hover:text-forest"
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.14}>
            <div className="mt-8">
              {tab === "site" ? <SiteEditor /> : <CollectionEditor key={tab} name={tab} />}
            </div>
          </Reveal>
        </div>
      </div>
    </AdminGuard>
  );
}
